import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/auth';
import { useLanguage } from '@/context/language-context';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Send, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { ScrollArea } from "@/components/ui/scroll-area"

interface Conversation {
  id: string;
  created_at: string;
  participant1_id: string;
  participant2_id: string;
  last_message_time: string;
}

interface Message {
  id: string;
  created_at: string;
  conversation_id: string;
  senderId: string;
  content: string;
}

const EscortMessages = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageContent, setMessageContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [partners, setPartners] = useState(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Mettre useCallback sur les fonctions utilisées dans useEffect
  const fetchConversations = useCallback(async () => {
    if (!user?.id) return; // Vérifier user.id ici
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .or(`participant1_id.eq.${user?.id},participant2_id.eq.${user?.id}`);

      if (error) {
        console.error('Error fetching conversations:', error);
        toast({
          title: t('common.error'),
          description: t('messages.error_creating'),
          variant: 'destructive',
        });
      } else {
        setConversations(data || []);
        // Fetch partner profiles
        if (data) {
          const partnerIds = new Set<string>();
          data.forEach(conversation => {
            if (conversation.participant1_id !== user?.id) {
              partnerIds.add(conversation.participant1_id);
            } else {
              partnerIds.add(conversation.participant2_id);
            }
          });
          fetchPartnerProfiles(Array.from(partnerIds));
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast({
        title: t('common.error'),
        description: t('messages.error_creating'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [supabase, user?.id, toast, t]);

  const fetchPartnerProfiles = async (partnerIds: string[]) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', partnerIds);

      if (error) {
        console.error('Error fetching partner profiles:', error);
      } else {
        const partnerMap = new Map();
        data?.forEach(profile => {
          partnerMap.set(profile.id, profile);
        });
        setPartners(partnerMap);
      }
    } catch (error) {
      console.error('Error fetching partner profiles:', error);
    }
  };

  // Mettre useCallback sur les fonctions utilisées dans useEffect
  const fetchMessages = useCallback(async (conversationId: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching messages:', error);
        toast({
          title: t('common.error'),
          description: t('messages.error_creating'),
          variant: 'destructive',
        });
      } else {
        setMessages(data || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: t('common.error'),
        description: t('messages.error_creating'),
        variant: 'destructive',
      });
    }
  }, [supabase, toast, t]);

  useEffect(() => {
    if (!user) return;
    fetchConversations();
  }, [user, fetchConversations]);

  useEffect(() => {
    if (currentConversation) {
      fetchMessages(currentConversation.id);
    }
  }, [currentConversation, fetchMessages]);

  useEffect(() => {
    // Scroll to bottom when messages change
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!messageContent.trim()) return;
    
    try {
      setIsSending(true);
      const message = {
        conversation_id: currentConversation?.id,
        senderId: user?.id,
        content: messageContent.trim(),
      };
      
      const { data, error } = await supabase
        .from('messages')
        .insert([message])
        .select('*');
      
      if (error) {
        console.error('Error sending message:', error);
        toast({
          title: t('common.error'),
          description: t('messages.error_creating'),
          variant: 'destructive',
        });
      } else {
        setMessages([...messages, data[0]]);
        setMessageContent('');
        
        // Update last message time in conversations
        const partnerId = currentConversation?.participant1_id === user?.id
          ? currentConversation.participant2_id
          : currentConversation.participant1_id;
        
        await supabase
          .from('conversations')
          .update({ last_message_time: new Date().toISOString() })
          .eq('id', currentConversation?.id);
        
        // Refresh conversations
        fetchConversations();
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: t('common.error'),
        description: t('messages.error_creating'),
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };
  
  const handleConversationClick = (conversation: Conversation) => {
    setCurrentConversation(conversation);
  };

  // Sort conversations by last message time
  const sortedConversations = [...conversations].sort((a, b) => {
    const aTime = a.last_message_time || a.created_at;
    const bTime = b.last_message_time || b.created_at;
    return new Date(bTime).getTime() - new Date(aTime).getTime();
  });

  // Group conversations by date
  const groupedConversations: { [key: string]: Conversation[] } = {};
  sortedConversations.forEach((conversation) => {
    const partner = conversation.participant1_id === user?.id
      ? partners.get(conversation.participant2_id)
      : partners.get(conversation.participant1_id);
    
    if (!partner) return;
    
    const date = new Date(conversation.last_message_time || conversation.created_at).toLocaleDateString();
    if (!groupedConversations[date]) {
      groupedConversations[date] = [];
    }
    groupedConversations[date].push(conversation);
  });

  const renderConversationItem = (conversation: Conversation) => {
    const partner = conversation.participant1_id === user?.id
      ? partners.get(conversation.participant2_id)
      : partners.get(conversation.participant1_id);
    
    if (!partner) return null;
    
    const isSelected = currentConversation?.id === conversation.id;
    
    return (
      <Button
        variant="ghost"
        className={`w-full justify-start rounded-none ${isSelected ? 'bg-secondary hover:bg-secondary' : 'hover:bg-accent'}`}
        onClick={() => handleConversationClick(conversation)}
        key={conversation.id}
      >
        <div className="grid grid-cols-[40px_1fr] items-center gap-2 py-2">
          <Avatar>
            <AvatarImage src={partner.avatar_url || undefined} alt={partner.display_name} />
            <AvatarFallback>{partner.display_name?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
          </Avatar>
          <div className="text-left">
            <div className="font-medium truncate">{partner.display_name || t('common.unknown_user')}</div>
            {renderConversationTime(conversation)}
          </div>
        </div>
      </Button>
    );
  };

  const renderConversationTime = (conversation: Conversation) => {
    const timeToDisplay = conversation.last_message_time 
      ? formatMessageTime(new Date(conversation.last_message_time))
      : formatMessageTime(new Date(conversation.created_at));
    
    return (
      <span className="text-xs text-gray-500">{timeToDisplay}</span>
    );
  };

  const renderMessage = (message: Message) => {
    const isOwnMessage = (message: Message) => message.senderId === user?.id;
    
    return (
      <div
        key={message.id}
        className={`flex w-full flex-col space-y-2 ${isOwnMessage(message) ? 'items-end' : 'items-start'}`}
      >
        <div className="space-x-2">
          <p className={`text-sm max-w-[80%] rounded-lg px-3 py-2 ${isOwnMessage(message) ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
            {message.content}
          </p>
        </div>
        <span className="text-xs text-gray-500">{formatMessageTime(new Date(message.created_at))}</span>
      </div>
    );
  };

  // Update the sender_id to senderId for consistency
  const isOwnMessage = (message: Message) => message.senderId === user?.id;

  const formatMessageTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 60) {
      return `${minutes} ${t('common.minutes_ago')}`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      return `${hours} ${t('common.hours_ago')}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (isLoading) {
    return <div>{t('common.loading')}...</div>;
  }

  return (
    <div className="container mx-auto h-full">
      <Card className="h-full">
        <CardHeader>
          <CardTitle>{t('messages.title')}</CardTitle>
          <CardDescription>{t('messages.description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex h-[calc(100%-100px)] space-x-4">
          {/* Conversation List */}
          <div className="w-1/4 border-r">
            <h3 className="mb-4 px-2 font-semibold">{t('messages.conversations')}</h3>
            <ScrollArea className="h-[calc(100%-50px)] pb-2">
              {Object.keys(groupedConversations).map((date) => (
                <div key={date} className="mb-4">
                  <p className="mx-2 mb-1 text-sm font-semibold">{date}</p>
                  {groupedConversations[date].map((conversation) => renderConversationItem(conversation))}
                </div>
              ))}
            </ScrollArea>
          </div>
          
          {/* Message Area */}
          <div className="flex h-full w-3/4 flex-col">
            {currentConversation ? (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4">
                  <ScrollArea className="h-[calc(100%-100px)] pb-2">
                    {messages.map((message) => renderMessage(message))}
                    <div ref={messagesEndRef} />
                  </ScrollArea>
                </div>
                
                {/* Message Input */}
                <div className="border-t p-4">
                  <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                    <Input
                      type="text"
                      placeholder={t('messages.type_message')}
                      value={messageContent}
                      onChange={(e) => setMessageContent(e.target.value)}
                      className="flex-1"
                    />
                    <Button type="submit" disabled={isSending}>
                      {isSending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {t('messages.send')}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-gray-500">{t('messages.select_conversation')}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EscortMessages;
